/**
 * Componente de card de preview de mídia
 * 
 * Exibe thumbnail/preview do arquivo com informações e ações
 * (visualizar, selecionar, deletar)
 */

import { useState } from 'react';
import { MediaGalleryItem, formatBytes, getFileTypeIcon } from '@/types/media';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { MoreVertical, Trash2, Eye, Check } from 'lucide-react';
import { format } from 'date-fns';

interface MediaPreviewCardProps {
  media: MediaGalleryItem;
  mediaUrl?: string;
  onDelete?: (mediaId: string) => void;
  onSelect?: (media: MediaGalleryItem) => void;
  isSelected?: boolean;
  selectionMode?: boolean;
}

export function MediaPreviewCard({
  media,
  mediaUrl,
  onDelete,
  onSelect,
  isSelected = false,
  selectionMode = false,
}: MediaPreviewCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Renderizar preview baseado no tipo de arquivo
  const renderPreview = () => {
    if (!mediaUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <span className="text-4xl">{getFileTypeIcon(media.file_type)}</span>
        </div>
      );
    }

    switch (media.file_type) {
      case 'image':
        return (
          <img
            src={mediaUrl}
            alt={media.name}
            className="w-full h-full object-cover"
          />
        );
      
      case 'video':
        return (
          <video
            src={mediaUrl}
            className="w-full h-full object-cover"
            muted
          />
        );
      
      case 'audio':
        return (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <span className="text-4xl">🎵</span>
          </div>
        );
      
      case 'document':
        return (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <span className="text-4xl">📄</span>
          </div>
        );
      
      default:
        return null;
    }
  };

  const handleCardClick = () => {
    if (selectionMode && onSelect) {
      onSelect(media);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(media.id);
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          'group relative rounded-lg border overflow-hidden transition-all',
          'hover:shadow-lg hover:scale-[1.02]',
          selectionMode && 'cursor-pointer',
          isSelected && 'ring-2 ring-primary ring-offset-2'
        )}
        onClick={handleCardClick}
      >
        {/* Preview */}
        <div className="aspect-square relative bg-muted overflow-hidden">
          {renderPreview()}
          
          {/* Overlay com ações */}
          <div
            className={cn(
              'absolute inset-0 bg-black/60 opacity-0 transition-opacity',
              'flex items-center justify-center gap-2',
              !selectionMode && 'group-hover:opacity-100'
            )}
          >
            {selectionMode ? (
              isSelected && (
                <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                  <Check className="h-4 w-4 text-primary-foreground" />
                </div>
              )
            ) : (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (mediaUrl) window.open(mediaUrl, '_blank');
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                
                {onDelete && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>

        {/* Informações */}
        <div className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium truncate flex-1" title={media.name}>
              {media.name}
            </p>
            <Badge variant="outline" className="text-xs shrink-0">
              {media.file_type}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatBytes(media.size_bytes)}</span>
            <span>{format(new Date(media.created_at), 'dd/MM/yyyy')}</span>
          </div>

          {/* Metadados adicionais */}
          {(media.metadata?.width || media.metadata?.duration) && (
            <div className="text-xs text-muted-foreground">
              {media.metadata.width && media.metadata.height && (
                <span>{media.metadata.width}×{media.metadata.height}px</span>
              )}
              {media.metadata.duration && (
                <span>{Math.round(media.metadata.duration)}s</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{media.name}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
